/**
 * Which part of the user manual explains each backoffice screen (work item 830). This is the one
 * place the screen-to-section map lives: the help panel opens a screen's own section and lists the
 * related ones as cross-references. help-topics.spec.ts fails if an entry names a page or section
 * the manual does not have, because the static host answers a missing page with the backoffice
 * itself and a 200, so a broken help link would never show up as an error.
 */

/** A section of the public manual: a page and the id of a heading on it. */
export interface HelpSectionRef {
  page: string;
  section: string;
  label: string;
}

/** The help for one screen. */
export interface HelpTopic {
  /** The route path, with :params as on the router, e.g. "collections/plans/:id". */
  route: string;
  /** The screen's name, shown as the panel title. */
  title: string;
  page: string;
  section: string;
  /** Cross-references for the screen, in the order a reader is likely to need them. */
  related: HelpSectionRef[];
}

const see = (page: string, section: string, label: string): HelpSectionRef => ({ page, section, label });

const WORKFLOW_RELATED = [
  see("backoffice-workflow.html", "item", "Working an item"),
  see("backoffice-workflow.html", "faulted", "Faulted items and Reset"),
  see("backoffice-onboarding.html", "automatic-checks", "Reviewing a wallet onboarding"),
  see("backoffice-loans.html", "approving", "Approving a loan application"),
];

const WORKFLOW_ITEM_RELATED = [
  see("backoffice-onboarding.html", "reading-the-page", "Reading the verification page"),
  see("backoffice-onboarding.html", "auto-onboarding", "The Auto-onboarding panel"),
  see("backoffice-onboarding.html", "decide", "Approve or reject an onboarding"),
  see("backoffice-onboarding.html", "fix-and-retry", "Fix and retry a contact detail"),
  see("backoffice-workflow.html", "faulted", "Faulted items and Reset"),
  see("backoffice-loans.html", "approving", "Approving a loan application"),
  see("backoffice-workflow.html", "merchant-verification", "Merchant Verification workflow"),
  see("backoffice-workflow.html", "claim", "Claim workflow"),
];

const MERCHANT_RELATED = [
  see("backoffice-merchants-suppliers.html", "bank-rules", "Bank account rules"),
  see("backoffice-onboarding.html", "automatic-checks", "How a wallet onboarding is checked"),
  see("backoffice-wallet.html", "profiles", "The merchant's wallet profile"),
  see("backoffice-loans.html", "contracts", "Loan contracts"),
];

const SUPPLIER_RELATED = [
  see("backoffice-merchants-suppliers.html", "supplier-types", "Supplier types"),
  see("backoffice-merchants-suppliers.html", "bank-rules", "Bank account rules"),
  see("backoffice-loans.html", "disbursing", "Disbursing to a supplier"),
];

const USER_RELATED = [
  see("backoffice-admin.html", "roles", "Roles and what they open"),
  see("backoffice-admin.html", "wallet-admin-users", "Wallet roles"),
  see("backoffice-navigation.html", "help", "Help and the technical manual"),
];

const PRODUCT_RELATED = [
  see("backoffice-collections.html", "how-it-works", "How collections work"),
  see("backoffice-collections.html", "discount", "Turnover discount"),
  see("backoffice-collections.html", "plans", "Collection plans"),
];

const PLAN_RELATED = [
  see("backoffice-collections.html", "how-it-works", "How collections work"),
  see("backoffice-collections.html", "default", "Arrears and default"),
  see("backoffice-collections.html", "discount", "Turnover discount"),
  see("backoffice-collections.html", "merchant-view", "What the merchant sees"),
  see("reversals.html", "collections", "Reversing a collection"),
];

const INVENTORY_RELATED = [
  see("backoffice-collections.html", "products", "Collection products"),
  see("backoffice-collections.html", "plans", "Collection plans"),
  see("backoffice-wallet.html", "devices", "Wallet devices"),
];

/**
 * Most specific first: the first entry whose route matches wins, so "collections/plans/new" must come
 * before "collections/plans/:id".
 */
export const HELP_TOPICS: HelpTopic[] = [
  {
    route: "dashboard", title: "Dashboard", page: "backoffice-dashboard.html", section: "tiles",
    related: [
      see("backoffice-dashboard.html", "panels", "The panels"),
      see("backoffice-dashboard.html", "needs-attention", "Needs attention"),
      see("backoffice-workflow.html", "tabs", "Workflow queues"),
    ],
  },
  { route: "workflow", title: "Workflow", page: "backoffice-workflow.html", section: "tabs", related: WORKFLOW_RELATED },
  { route: "workflow/:group", title: "Workflow", page: "backoffice-workflow.html", section: "tabs", related: WORKFLOW_RELATED },
  { route: "workflow/:group/:id", title: "Workflow item", page: "backoffice-workflow.html", section: "item", related: WORKFLOW_ITEM_RELATED },
  {
    route: "applications", title: "Loan applications", page: "backoffice-loans.html", section: "application-lifecycle",
    related: [
      see("backoffice-loans.html", "approving", "Approving an application"),
      see("backoffice-loans.html", "product", "The product"),
      see("backoffice-workflow.html", "application-processing", "Application Processing workflow"),
    ],
  },
  {
    route: "applications/byid/:id", title: "Loan application", page: "backoffice-loans.html", section: "approving",
    related: [
      see("backoffice-loans.html", "application-lifecycle", "How an application moves"),
      see("backoffice-loans.html", "product", "The product"),
      see("backoffice-merchants-suppliers.html", "merchants", "Merchants"),
    ],
  },
  {
    route: "contracts", title: "Contracts", page: "backoffice-loans.html", section: "contracts",
    related: [
      see("backoffice-loans.html", "contract-tabs", "The contract page"),
      see("backoffice-loans.html", "daily-collections", "Daily collections"),
      see("backoffice-loans.html", "repayments", "Recording a repayment"),
    ],
  },
  {
    route: "contracts/:id", title: "Contract", page: "backoffice-loans.html", section: "contract-tabs",
    related: [
      see("backoffice-loans.html", "disbursing", "Disbursing"),
      see("backoffice-loans.html", "repayments", "Recording a repayment"),
      see("backoffice-loans.html", "hold-restructure", "Hold and restructure"),
      see("backoffice-loans.html", "daily-collections", "Daily collections"),
    ],
  },
  { route: "merchants", title: "Merchants", page: "backoffice-merchants-suppliers.html", section: "merchants", related: MERCHANT_RELATED },
  { route: "merchants/:id", title: "Merchant", page: "backoffice-merchants-suppliers.html", section: "merchants", related: MERCHANT_RELATED },
  { route: "suppliers", title: "Suppliers", page: "backoffice-merchants-suppliers.html", section: "suppliers", related: SUPPLIER_RELATED },
  // Before "suppliers/:id", which would otherwise match "types" as a supplier id (work item 926).
  { route: "suppliers/types", title: "Supplier types", page: "backoffice-merchants-suppliers.html", section: "supplier-types", related: SUPPLIER_RELATED },
  { route: "suppliers/:id", title: "Supplier", page: "backoffice-merchants-suppliers.html", section: "suppliers", related: SUPPLIER_RELATED },
  {
    route: "municipalities", title: "Municipalities", page: "backoffice-admin.html", section: "municipalities",
    related: [see("backoffice-admin.html", "csv-import", "Importing a CSV")],
  },
  { route: "users", title: "Users", page: "backoffice-admin.html", section: "users", related: USER_RELATED },
  { route: "users/:id", title: "User", page: "backoffice-admin.html", section: "users", related: USER_RELATED },
  {
    route: "roles", title: "Roles", page: "backoffice-admin.html", section: "roles",
    related: [see("backoffice-admin.html", "users", "Giving a user a role")],
  },
  {
    route: "roles/:name", title: "Role", page: "backoffice-admin.html", section: "roles",
    related: [see("backoffice-admin.html", "users", "Giving a user a role")],
  },
  { route: "collections/products", title: "Collection products", page: "backoffice-collections.html", section: "products", related: PRODUCT_RELATED },
  { route: "collections/products/:id", title: "Collection product", page: "backoffice-collections.html", section: "products", related: PRODUCT_RELATED },
  { route: "collections/plans", title: "Collection plans", page: "backoffice-collections.html", section: "plans", related: PLAN_RELATED },
  { route: "collections/plans/new", title: "Enrol a merchant", page: "backoffice-collections.html", section: "plans", related: PLAN_RELATED },
  { route: "collections/plans/:id", title: "Collection plan", page: "backoffice-collections.html", section: "plans", related: PLAN_RELATED },
  {
    route: "collections/run", title: "Collection runs", page: "backoffice-collections.html", section: "runs",
    related: [
      see("backoffice-collections.html", "how-it-works", "How collections work"),
      see("backoffice-collections.html", "default", "Arrears and default"),
      see("reversals.html", "collections", "Reversing a collection"),
    ],
  },
  { route: "collections/inventory", title: "Device inventory", page: "backoffice-collections.html", section: "inventory", related: INVENTORY_RELATED },
  { route: "collections/inventory/load", title: "Load devices", page: "backoffice-collections.html", section: "inventory", related: INVENTORY_RELATED },
  { route: "collections/inventory/:id", title: "Device", page: "backoffice-collections.html", section: "inventory", related: INVENTORY_RELATED },
  {
    route: "management-system-users", title: "Wallet profiles", page: "backoffice-wallet.html", section: "profiles",
    related: [
      see("backoffice-wallet.html", "edit-profile", "Editing a profile"),
      see("backoffice-wallet.html", "default", "Putting a merchant into default"),
      see("statements.html", "getting-one", "Getting a statement"),
    ],
  },
  {
    route: "management-system-wallet", title: "Wallets", page: "backoffice-wallet.html", section: "wallets",
    related: [
      see("backoffice-wallet.html", "transactions", "Transactions and statements"),
      see("payments.html", "ledger", "How the ledger works"),
    ],
  },
  {
    route: "management-system-transaction", title: "Transactions", page: "backoffice-wallet.html", section: "transactions",
    related: [
      see("statements.html", "transaction-types", "Transaction types"),
      see("payments.html", "payshap", "PayShap"),
      see("reversals.html", "failed-bank-payments", "Failed bank payments"),
      see("fees-and-limits.html", "fees", "Transaction fees"),
    ],
  },
  {
    route: "management-system-device", title: "Devices", page: "backoffice-wallet.html", section: "devices",
    related: [see("backoffice-collections.html", "inventory", "Device inventory")],
  },
  {
    route: "wallet-card-payments", title: "Card payments to credit", page: "backoffice-wallet.html", section: "card-payments",
    related: [
      see("backoffice-wallet.html", "devices", "Devices"),
      see("backoffice-dashboard.html", "needs-attention", "Needs attention on the dashboard"),
      see("backoffice-device-lookup.html", "using", "Device lookup"),
    ],
  },
  {
    route: "wallet-device-lookup", title: "Device lookup", page: "backoffice-device-lookup.html", section: "using",
    related: [
      see("backoffice-device-lookup.html", "outcomes", "What each outcome means"),
      see("backoffice-wallet.html", "card-payments", "Card payments to credit"),
      see("backoffice-wallet.html", "devices", "Registering a device"),
      see("backoffice-wallet.html", "edit-profile", "Editing a profile"),
    ],
  },
  {
    route: "wallet-user-admin", title: "Wallet admin users", page: "backoffice-admin.html", section: "wallet-admin-users",
    related: [
      see("backoffice-admin.html", "users", "Backoffice users"),
      see("backoffice-admin.html", "roles", "Backoffice roles"),
    ],
  },
  {
    route: "release-history", title: "Release history", page: "backoffice-navigation.html", section: "help",
    related: [see("backoffice-navigation.html", "menu", "Finding your way around")],
  },
  {
    route: "technical-manual", title: "Technical manual", page: "backoffice-navigation.html", section: "help",
    related: [see("backoffice-admin.html", "roles", "Who can read it: roles")],
  },
  {
    route: "technical-manual/:page", title: "Technical manual", page: "backoffice-navigation.html", section: "help",
    related: [see("backoffice-admin.html", "roles", "Who can read it: roles")],
  },
];

/** The help for any screen not listed: how the backoffice is laid out. */
export const FALLBACK_TOPIC: HelpTopic = {
  route: "",
  title: "Using the backoffice",
  page: "backoffice-navigation.html",
  section: "menu",
  related: [
    see("backoffice-navigation.html", "common-controls", "Controls on every page"),
    see("backoffice-navigation.html", "help", "Getting help"),
    see("getting-started.html", "staff", "Getting started as backoffice staff"),
  ],
};

/** The help topic for a router URL; query strings and fragments are ignored. */
export function resolveHelpTopic(url: string): HelpTopic {
  const segments = url.split(/[?#]/)[0].split("/").filter(Boolean);
  return HELP_TOPICS.find((topic) => matches(topic.route, segments)) ?? FALLBACK_TOPIC;
}

function matches(route: string, segments: string[]): boolean {
  const parts = route.split("/").filter(Boolean);
  return parts.length === segments.length
    && parts.every((part, i) => part.startsWith(":") || part === segments[i]);
}

/** The ids each public page offers, as written to docs/user-manual/assets/anchors.json by the index builder. */
export interface ManualAnchors {
  pages: Record<string, string[]>;
}

/** Every reference in the topics that names a missing page or section; empty when all resolve. */
export function findBrokenReferences(topics: HelpTopic[], anchors: ManualAnchors): string[] {
  const problems: string[] = [];
  const check = (where: string, page: string, section: string) => {
    const ids = anchors.pages[page];
    if (!ids) problems.push(`${where}: no page ${page}`);
    else if (!ids.includes(section)) problems.push(`${where}: no section ${page}#${section}`);
  };
  for (const topic of topics) {
    const where = topic.route || "(fallback)";
    check(where, topic.page, topic.section);
    topic.related.forEach((r) => check(`${where} related "${r.label}"`, r.page, r.section));
  }
  return problems;
}
